import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { useConfig } from "../context/ConfigContext";
import {
  Calendar,
  Clock,
  DollarSign,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  Car,
  Utensils,
} from "lucide-react";

// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const stripePromise = "";

// Fetch the Stripe public key from the backend on mount
const { data } = await axios.get("/api/config/stripe");
if (data.publicKey) {
  // Create the promise with the public key
  const stripePromise = await loadStripe(data.publicKey);
} else {
  throw new Error("No public key in response");
}

const CustomDatePicker = ({
  selectedDate,
  onDateChange,
  minDate,
  maxDate,
  availableDays = [],
  config,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);

  // Get the current date for highlighting today
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Parse dates for comparison
  const minDateTime = minDate ? new Date(minDate) : null;
  const maxDateTime = maxDate ? new Date(maxDate) : null;
  const selectedDateTime = selectedDate ? new Date(selectedDate) : null;

  // Get available dates for the current month (only show dates that are available)
  const getAvailableDatesInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const availableDates = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      if (isDateAvailable(currentDate)) {
        availableDates.push(currentDate);
      }
    }

    return availableDates;
  };

  // Check if date is available
  const isDateAvailable = (date) => {
    if (!date) return false;

    // Check min/max bounds
    if (minDateTime && date < minDateTime) return false;
    if (maxDateTime && date > maxDateTime) return false;

    // Check available days if specified
    if (availableDays.length > 0) {
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayName = dayNames[date.getDay()];
      if (!availableDays.includes(dayName)) return false;
    }

    return true;
  };

  // Check if date is selected
  const isDateSelected = (date) => {
    if (!date || !selectedDateTime) return false;
    return date.toDateString() === selectedDateTime.toDateString();
  };

  // Check if date is today
  const isToday = (date) => {
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    const dateString = date.toISOString().split("T")[0];
    onDateChange(dateString);
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  // Check if we can navigate to next/previous month
  const canNavigatePrev = () => {
    if (!minDateTime) return true;
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(currentMonth.getMonth() - 1);
    return (
      prevMonth.getFullYear() > minDateTime.getFullYear() ||
      (prevMonth.getFullYear() === minDateTime.getFullYear() &&
        prevMonth.getMonth() >= minDateTime.getMonth())
    );
  };

  const canNavigateNext = () => {
    if (!maxDateTime) return true;
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(currentMonth.getMonth() + 1);
    return (
      nextMonth.getFullYear() < maxDateTime.getFullYear() ||
      (nextMonth.getFullYear() === maxDateTime.getFullYear() &&
        nextMonth.getMonth() <= maxDateTime.getMonth())
    );
  };

  const availableDates = getAvailableDatesInMonth(currentMonth);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  return (
    <div className="card-date-picker">
      {/* Header with icon and title */}
      {/* <div className="picker-header">
        <div className="header-icon">
          <Calendar className="w-6 h-6" />
        </div>
        <h3 className="header-title">Choose Your Date</h3>
      </div> */}

      {/* Month Navigation */}
      <div className="month-navigation">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          disabled={!canNavigatePrev()}
          className="nav-button"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>

        <h4 className="month-title">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h4>

        <button
          type="button"
          onClick={() => navigateMonth(1)}
          disabled={!canNavigateNext()}
          className="nav-button"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="9,6 15,12 9,18"></polyline>
          </svg>
        </button>
      </div>

      {/* Available Dates Grid */}
      <div className="dates-container">
        {availableDates.length === 0 ? (
          <div className="no-dates">
            <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm text-center">
              No available dates in {monthNames[currentMonth.getMonth()]}
            </p>
          </div>
        ) : (
          <div className="dates-grid grid grid-cols-6 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 xl:grid-cols-8">
            {availableDates.map((date, index) => (
              <div
                key={index}
                className={`date-card ${
                  isDateSelected(date) ? "selected" : ""
                } ${isToday(date) ? "today" : ""} ${
                  hoveredDate &&
                  date.toDateString() === hoveredDate.toDateString()
                    ? "hovered"
                    : ""
                }`}
                onClick={() => handleDateSelect(date)}
                onMouseEnter={() => setHoveredDate(date)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                <div className="date-number">{date.getDate()}</div>
                <div className="date-month">{monthNames[date.getMonth()]}</div>
                <div className="date-day">{dayNames[date.getDay()]}</div>
                {isToday(date) && <div className="today-badge">Today</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .card-date-picker {
          width: 100%;
          background: white;
          border-radius: 16px;
          padding: 1.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
          border: 1px solid #e5e7eb;
        }

        .picker-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .header-icon {
          width: 40px;
          height: 40px;
          background: ${config?.colors?.primary || "#6366f1"};
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .header-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: ${config?.colors?.textPrimary || "#111827"};
          margin: 0;
        }

        .month-navigation {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          padding: 0 0.5rem;
        }

        .nav-button {
          width: 36px;
          height: 36px;
          border: 2px solid ${config?.colors?.primary || "#6366f1"};
          background: transparent;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          color: ${config?.colors?.primary || "#6366f1"};
        }

        .nav-button:hover:not(:disabled) {
          background: ${config?.colors?.primary || "#6366f1"};
          color: white;
          transform: scale(1.05);
        }

        .nav-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          transform: none;
        }

        .month-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: ${config?.colors?.textPrimary || "#111827"};
          margin: 0;
          text-align: center;
        }

        .dates-container {
          min-height: 200px;
        }

        .no-dates {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
        }

        .dates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 0.75rem;
          max-height: 400px;
          overflow-y: auto;
          padding-right: 4px;
        }

        .dates-grid::-webkit-scrollbar {
          width: 4px;
        }

        .dates-grid::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 2px;
        }

        .dates-grid::-webkit-scrollbar-thumb {
          background: ${config?.colors?.primary || "#6366f1"};
          border-radius: 2px;
        }

        .date-card {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 1rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          min-height: 80px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .date-card:hover,
        .date-card.hovered {
          border-color: ${config?.colors?.primary || "#6366f1"};
          background: linear-gradient(
            135deg,
            ${config?.colors?.primary || "#6366f1"}10 0%,
            ${config?.colors?.accent || "#8b5cf6"}10 100%
          );
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(99, 102, 241, 0.15);
        }

        .date-card.selected {
          background: linear-gradient(
            135deg,
            ${config?.colors?.primary || "#6366f1"} 0%,
            ${config?.colors?.accent || "#8b5cf6"} 100%
          );
          border-color: ${config?.colors?.primary || "#6366f1"};
          color: white;
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 10px 30px rgba(99, 102, 241, 0.3);
        }

        .date-card.today:not(.selected) {
          border-color: ${config?.colors?.secondary || "#f59e0b"};
          background: linear-gradient(
            135deg,
            ${config?.colors?.secondary || "#f59e0b"}15 0%,
            #fbbf2415 100%
          );
        }

        .date-number {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1;
          color: inherit;
        }

        .date-month {
          font-size: 0.75rem;
          font-weight: 500;
          margin-top: 0.25rem;
          opacity: 0.8;
          color: inherit;
        }

        .date-day {
          font-size: 0.7rem;
          font-weight: 500;
          margin-top: 0.125rem;
          opacity: 0.7;
          color: inherit;
        }

        .today-badge {
          position: absolute;
          top: 4px;
          right: 4px;
          background: ${config?.colors?.secondary || "#f59e0b"};
          color: white;
          font-size: 0.6rem;
          font-weight: 600;
          padding: 0.125rem 0.375rem;
          border-radius: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .date-card.selected .today-badge {
          background: rgba(255, 255, 255, 0.3);
        }

        @media (max-width: 768px) {
          .card-date-picker {
            padding: 1rem;
          }

          .dates-grid {
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 0.5rem;
          }

          .date-card {
            padding: 0.75rem;
            min-height: 70px;
          }

          .date-number {
            font-size: 1.25rem;
          }

          .date-month {
            font-size: 0.7rem;
          }

          .date-day {
            font-size: 0.65rem;
          }

          .header-title {
            font-size: 1.1rem;
          }

          .month-title {
            font-size: 1rem;
          }
        }

        @media (max-width: 480px) {
          .dates-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .date-card {
            min-height: 60px;
            padding: 0.5rem;
          }

          .picker-header {
            margin-bottom: 1rem;
          }

          .month-navigation {
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

const BookingForm = ({ service }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { config } = useConfig();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [duration, setDuration] = useState(
    service?.timeSlots?.[0]?.duration || 60
  );
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [availableDurations, setAvailableDurations] = useState([]);

  const [customerInfo, setCustomerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
  });

  const [formResponses, setFormResponses] = useState({});
  const [calculatedPrice, setCalculatedPrice] = useState({
    basePrice: 0,
    optionPrices: 0,
    subtotal: 0,
    gst: 0,
    pst: 0,
    totalAmount: 0,
  });

  // Step configuration
  const steps = [
    { id: 1, label: "Date & Time", icon: Calendar },
    { id: 2, label: "Information", icon: User },
    { id: 3, label: "Details", icon: FileText },
    { id: 4, label: "Payment", icon: CreditCard },
  ];

  // Calculate progress percentage
  const getProgressPercentage = () => {
    return ((currentStep - 1) / (steps.length - 1)) * 100;
  };

  // Check if step is complete
  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 1:
        return selectedDate && selectedTimeSlot;
      case 2:
        return (
          customerInfo.firstName && customerInfo.lastName && customerInfo.email
        );
      case 3:
        const requiredFields =
          service?.formFields?.filter((field) => field.required) || [];
        return requiredFields.every((field) => {
          const value = formResponses[field.fieldId];
          return value && (Array.isArray(value) ? value.length > 0 : true);
        });
      case 4:
        return !service?.requiresPayment || calculatedPrice.totalAmount === 0;
      default:
        return false;
    }
  };

  // Navigation functions
  const canGoNext = () => {
    return isStepComplete(currentStep);
  };

  const nextStep = () => {
    if (canGoNext() && currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setError("");
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError("");
    }
  };

  // Set up available durations based on service type
  useEffect(() => {
    if (service) {
      if (
        service.category === "driving_class" ||
        service.pricing.pricingType === "duration_based"
      ) {
        const serviceDurations = service.timeSlots.map((slot) => slot.duration);
        const uniqueDurations = [...new Set(serviceDurations)].sort(
          (a, b) => a - b
        );
        setAvailableDurations(uniqueDurations);
        setDuration(uniqueDurations[0] || 60);
      } else {
        setAvailableDurations([service.timeSlots[0]?.duration || 60]);
        setDuration(service.timeSlots[0]?.duration || 60);
      }
    }
  }, [service]);

  // Load available time slots when date or duration changes
  useEffect(() => {
    if (selectedDate && service?.slug) {
      loadAvailableSlots();
    }
  }, [selectedDate, duration, service?.slug]);

  // Calculate price when form responses change
  useEffect(() => {
    calculatePrice();
  }, [formResponses, duration, service]);

  const getMinDate = () => {
    const now = new Date();
    const minNoticeMs = (service?.minNoticeHours || 2) * 60 * 60 * 1000;
    const minDate = new Date(now.getTime() + minNoticeMs);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDateOnly = new Date(minDate);
    minDateOnly.setHours(0, 0, 0, 0);

    if (minDateOnly <= today) {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split("T")[0];
    }

    return minDate.toISOString().split("T")[0];
  };

  const getMaxDate = () => {
    const now = new Date();
    const maxDays = service?.advanceBookingDays || 30;
    const maxDate = new Date();
    maxDate.setDate(now.getDate() + maxDays);
    return maxDate.toISOString().split("T")[0];
  };

  const loadAvailableSlots = async () => {
    setLoading(true);
    setSelectedTimeSlot(null);
    try {
      const response = await axios.get(
        `/api/booking-services/${service.slug}/availability/${selectedDate}?duration=${duration}`
      );

      if (response.data.available) {
        setAvailableSlots(response.data.slots);
        setError("");
      } else {
        setAvailableSlots([]);
        setError(response.data.reason || "No available slots for this date");
      }
    } catch (error) {
      console.error("Error loading slots:", error);
      setError("Error loading available time slots");
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    if (!service) return;

    let basePrice = service.pricing.basePrice || 0;
    let optionPrices = 0;

    if (service.pricing.pricingType === "duration_based") {
      basePrice = (duration / 60) * (service.pricing.pricePerMinute || 0);
    }

    if (service.pricing.optionPricing && formResponses) {
      service.pricing.optionPricing.forEach((optionPrice) => {
        const responseValue = formResponses[optionPrice.fieldId];
        if (responseValue === optionPrice.optionValue) {
          optionPrices += optionPrice.additionalPrice || 0;
        }
      });
    }

    const subtotal = basePrice + optionPrices;
    const gst = service.hasGST ? subtotal * 0.05 : 0;
    const pst = service.hasPST ? subtotal * 0.07 : 0;
    const totalAmount = subtotal + gst + pst;

    setCalculatedPrice({
      basePrice,
      optionPrices,
      subtotal,
      gst,
      pst,
      totalAmount,
    });
  };

  const handleFormResponseChange = (fieldId, value) => {
    setFormResponses((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const validateForm = () => {
    if (
      !customerInfo.firstName ||
      !customerInfo.lastName ||
      !customerInfo.email
    ) {
      setError("Please fill in all required customer information");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (customerInfo.phone && !/^[\d\s\-\+\(\)\.]+$/.test(customerInfo.phone)) {
      setError("Please enter a valid phone number");
      return false;
    }

    if (!selectedDate || !selectedTimeSlot) {
      setError("Please select a date and time slot");
      return false;
    }

    const requiredFields = service.formFields.filter((field) => field.required);
    for (const field of requiredFields) {
      const value = formResponses[field.fieldId];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        setError(`Please fill in the required field: ${field.label}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setBookingLoading(true);
    setError("");

    try {
      let paymentMethodId = null;

      if (service.requiresPayment && calculatedPrice.totalAmount > 0) {
        if (!stripe || !elements) {
          setError("Payment system not ready. Please try again.");
          setBookingLoading(false);
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          setError("Payment form not found. Please refresh and try again.");
          setBookingLoading(false);
          return;
        }

        const { error: paymentError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: `${customerInfo.firstName} ${customerInfo.lastName}`,
              email: customerInfo.email,
              phone: customerInfo.phone || undefined,
            },
          });

        if (paymentError) {
          setError(paymentError.message);
          setBookingLoading(false);
          return;
        }

        paymentMethodId = paymentMethod.id;
      }

      const bookingData = {
        serviceSlug: service.slug,
        customerInfo,
        bookingDate: selectedDate + "T00:00:00",
        startTime: selectedTimeSlot.startTime,
        duration,
        formResponses: Object.entries(formResponses).map(
          ([fieldId, value]) => ({
            fieldId,
            value,
            label:
              service.formFields.find((f) => f.fieldId === fieldId)?.label ||
              fieldId,
          })
        ),
        paymentMethodId,
      };

      const response = await axios.post("/api/bookings", bookingData);

      if (response.data.success) {
        setBookingResult(response.data.booking);
        setSuccess(true);
        navigate(
          `/booking-confirmation/${response.data.booking.bookingNumber}?email=${customerInfo.email}`
        );
      } else {
        setError("Booking failed. Please try again.");
      }
    } catch (error) {
      console.error("Booking error:", error);
      setError(
        error.response?.data?.error ||
          "An error occurred while creating your booking"
      );
    } finally {
      setBookingLoading(false);
    }
  };

  const renderFormField = (field) => {
    const value = formResponses[field.fieldId] || "";

    switch (field.type) {
      case "text":
      case "email":
      case "phone":
        return (
          <input
            type={field.type}
            value={value}
            onChange={(e) =>
              handleFormResponseChange(field.fieldId, e.target.value)
            }
            placeholder={field.placeholder}
            required={field.required}
            className="form-input"
          />
        );

      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) =>
              handleFormResponseChange(field.fieldId, e.target.value)
            }
            placeholder={field.placeholder}
            required={field.required}
            rows="3"
            className="form-input"
          />
        );

      case "select":
        return (
          <select
            value={value}
            onChange={(e) =>
              handleFormResponseChange(field.fieldId, e.target.value)
            }
            required={field.required}
            className="form-input"
          >
            <option value="">Select an option...</option>
            {field.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div className="space-y-2">
            {field.options.map((option, index) => (
              <label key={index} className="flex items-center space-x-2">
                <input
                  type="radio"
                  name={field.fieldId}
                  value={option}
                  checked={value === option}
                  onChange={(e) =>
                    handleFormResponseChange(field.fieldId, e.target.value)
                  }
                  required={field.required}
                  className="text-blue-600"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div className="space-y-2">
            {field.options.map((option, index) => (
              <label key={index} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value={option}
                  checked={
                    Array.isArray(value) ? value.includes(option) : false
                  }
                  onChange={(e) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    handleFormResponseChange(field.fieldId, newValues);
                  }}
                  className="text-blue-600 rounded"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "driving_class":
        return <Car className="w-5 h-5" />;
      case "restaurant":
        return <Utensils className="w-5 h-5" />;
      default:
        return <Calendar className="w-5 h-5" />;
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Booking Confirmed!
          </h2>
          <p className="text-gray-600 mb-6">
            Your booking has been successfully created.
          </p>
          {bookingResult && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold mb-2">Booking Details</h3>
              <p>
                <strong>Booking Number:</strong> {bookingResult.bookingNumber}
              </p>
              <p>
                <strong>Status:</strong> {bookingResult.status}
              </p>
              {bookingResult.totalAmount > 0 && (
                <p>
                  <strong>Amount:</strong> $
                  {bookingResult.totalAmount.toFixed(2)}
                </p>
              )}
            </div>
          )}
          <button
            onClick={() => navigate("/")}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="booking-page"
      style={{
        "--primary": config?.colors?.primary || "#6366f1",
        "--primary-light": config?.colors?.accent || "#8b5cf6",
        "--secondary": config?.colors?.secondary || "#f59e0b",
        "--dark": config?.colors?.textPrimary || "#0f172a",
        "--text-primary": config?.colors?.textPrimary || "#111827",
        "--text-secondary": config?.colors?.textSecondary || "#6b7280",
      }}
    >
      {/* Hero Section */}
      <section
        className="booking-hero"
        style={{
          backgroundImage: service?.heroImage
            ? `url(${service.heroImage})`
            : "var(--gradient-1)",
          marginTop: 0,
        }}
      >
        <div className="booking-hero-content">
          <h1>Book Your {service?.name}</h1>
          <p>
            {service?.description ||
              "Follow the step-by-step process to schedule your perfect experience."}
          </p>
        </div>
      </section>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar">
          <div className="progress-line">
            <div
              className="progress-line-fill"
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
          {steps.map((step) => (
            <div key={step.id} className="progress-step">
              <div
                className={`step-circle ${
                  currentStep === step.id ? "active" : ""
                } ${isStepComplete(step.id) ? "completed" : ""}`}
              >
                {isStepComplete(step.id) ? "✓" : step.id}
              </div>
              <div
                className={`step-label ${
                  currentStep === step.id ? "active" : ""
                }`}
              >
                {step.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="booking-container">
        <div className="booking-grid">
          {/* Booking Form */}
          <div className="booking-form">
            {/* Step 1: Date & Time Selection */}
            {currentStep === 1 && (
              <div className="form-section active">
                <h2 className="section-title font-normal text-sm">
                  <div className="section-icon">
                    <Calendar className="w-5 h-5" />
                  </div>
                  Select Date & Time
                </h2>

                {/* Available Days Info */}
                {service?.availableDays && service.availableDays.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-start">
                      <Info className="w-4 h-4 text-blue-500 mr-2 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-700 font-medium">
                          Available Days:
                        </p>
                        <p className="text-sm text-blue-600">
                          {service.availableDays
                            .map(
                              (day) =>
                                day.charAt(0).toUpperCase() + day.slice(1)
                            )
                            .join(", ")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <p className="mt-4">
                  {" "}
                  <span> </span>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4">
                  {availableDurations.length > 1 && (
                    <div>
                      <label className="form-label">Duration (minutes) *</label>
                      <select
                        value={duration}
                        onChange={(e) => setDuration(parseInt(e.target.value))}
                        className="form-input"
                      >
                        {availableDurations.map((dur) => (
                          <option key={dur} value={dur}>
                            {dur} minutes ({(dur / 60).toFixed(1)} hours)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* <div>
                    <label className="form-label">Date *</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={getMinDate()}
                      max={getMaxDate()}
                      required
                      className="form-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available {service?.minNoticeHours}+ hours in advance
                    </p>
                  </div> */}

                  <div>
                    <label className="form-label">Date *</label>
                    <CustomDatePicker
                      selectedDate={selectedDate}
                      onDateChange={setSelectedDate}
                      minDate={getMinDate()}
                      maxDate={getMaxDate()}
                      availableDays={service?.availableDays || []}
                      config={config}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available {service?.minNoticeHours}+ hours in advance
                    </p>
                  </div>
                </div>

                {/* Available Time Slots */}
                {selectedDate && (
                  <div>
                    <label className="form-label">Available Time Slots *</label>
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                        <span className="ml-2">Loading available slots...</span>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="time-grid">
                        {availableSlots.map((slot, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setSelectedTimeSlot(slot)}
                            className={`time-card ${
                              selectedTimeSlot?.startTime === slot.startTime
                                ? "selected"
                                : ""
                            }`}
                          >
                            <div className="font-weight-600 text-lg">
                              {slot.startTime}
                            </div>
                            <div className="text-xs opacity-75">
                              to {slot.endTime}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        <Clock className="w-8 h-8 mx-auto mb-2" />
                        <p>{error || "No available slots for this date"}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Customer Information
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Customer Information */}
            {currentStep === 2 && (
              <div className="form-section active">
                <h2 className="section-title">
                  <div className="section-icon">
                    <User className="w-5 h-5" />
                  </div>
                  Customer Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      value={customerInfo.firstName}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          firstName: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      value={customerInfo.lastName}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          lastName: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          email: e.target.value,
                        })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          phone: e.target.value,
                        })
                      }
                      className="form-input"
                    />
                  </div>

                  {service?.category === "driving_class" && (
                    <div className="form-group md:col-span-2">
                      <label className="form-label">Company (Optional)</label>
                      <input
                        type="text"
                        value={customerInfo.company}
                        onChange={(e) =>
                          setCustomerInfo({
                            ...customerInfo,
                            company: e.target.value,
                          })
                        }
                        className="form-input"
                        placeholder="Company name (if applicable)"
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    Next: Additional Details
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Custom Form Fields */}
            {currentStep === 3 && (
              <div className="form-section active">
                <h2 className="section-title">
                  <div className="section-icon">
                    <FileText className="w-5 h-5" />
                  </div>
                  Additional Information
                </h2>

                {service?.formFields && service.formFields.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {service.formFields.map((field) => (
                      <div
                        key={field.fieldId}
                        className={`form-group ${
                          field.type === "textarea" ? "md:col-span-2" : ""
                        }`}
                      >
                        <label className="form-label">
                          {field.label}{" "}
                          {field.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        {renderFormField(field)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    <p>No additional information required.</p>
                  </div>
                )}

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!canGoNext()}
                    className="submit-btn"
                  >
                    {service?.requiresPayment && calculatedPrice.totalAmount > 0
                      ? "Next: Payment"
                      : "Complete Booking"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Payment */}
            {currentStep === 4 &&
              service?.requiresPayment &&
              calculatedPrice.totalAmount > 0 && (
                <div className="form-section active">
                  <h2 className="section-title">
                    <div className="section-icon">
                      <CreditCard className="w-5 h-5" />
                    </div>
                    Payment Information
                  </h2>

                  <div className="max-w-md">
                    <div className="mb-3">
                      <label className="form-label">Card Details</label>
                    </div>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#aab7c4",
                            },
                          },
                        },
                      }}
                      className="p-3 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Your payment information is secure and encrypted.
                    </p>
                  </div>

                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={bookingLoading}
                      className="submit-btn"
                    >
                      {bookingLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-5 h-5 mr-2" />
                      )}
                      {bookingLoading ? "Processing..." : "Complete Booking"}
                    </button>
                  </div>
                </div>
              )}

            {/* Complete booking for free services */}
            {currentStep === 4 &&
              (!service?.requiresPayment ||
                calculatedPrice.totalAmount === 0) && (
                <div className="form-section active">
                  <h2 className="section-title">
                    <div className="section-icon">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    Complete Booking
                  </h2>

                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <p className="text-lg text-gray-700 mb-6">
                      Review your booking details and complete your reservation.
                    </p>
                  </div>

                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={prevStep}
                      className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={bookingLoading}
                      className="submit-btn"
                    >
                      {bookingLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="w-5 h-5 mr-2" />
                      )}
                      {bookingLoading ? "Processing..." : "Complete Booking"}
                    </button>
                  </div>
                </div>
              )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start mt-4">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-red-700">{error}</span>
              </div>
            )}
          </div>

          {/* Booking Summary */}
          <div className="booking-summary">
            <h2 className="summary-title">Booking Summary</h2>

            <div className={`summary-item ${service?.name ? "filled" : ""}`}>
              <span className="summary-label">Service:</span>
              <span className="summary-value">
                {service?.name || "Select a service"}
              </span>
            </div>

            <div className={`summary-item ${selectedDate ? "filled" : ""}`}>
              <span className="summary-label">Date:</span>
              <span className="summary-value">
                {selectedDate
                  ? new Date(selectedDate).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "Select a date"}
              </span>
            </div>

            <div className={`summary-item ${selectedTimeSlot ? "filled" : ""}`}>
              <span className="summary-label">Time:</span>
              <span className="summary-value">
                {selectedTimeSlot
                  ? `${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime}`
                  : "Select a time"}
              </span>
            </div>

            <div className={`summary-item ${duration ? "filled" : ""}`}>
              <span className="summary-label">Duration:</span>
              <span className="summary-value">
                {duration ? `${duration} minutes` : "N/A"}
              </span>
            </div>

            {service?.requiresPayment && (
              <div className="summary-item price">
                <span className="summary-label">Total Price:</span>
                <span className="summary-value">
                  ${calculatedPrice.totalAmount.toFixed(2)}
                </span>
              </div>
            )}

            {calculatedPrice.totalAmount > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium mb-3">Price Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base Price:</span>
                    <span>${calculatedPrice.basePrice.toFixed(2)}</span>
                  </div>
                  {calculatedPrice.optionPrices > 0 && (
                    <div className="flex justify-between">
                      <span>Options:</span>
                      <span>${calculatedPrice.optionPrices.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>${calculatedPrice.subtotal.toFixed(2)}</span>
                  </div>
                  {calculatedPrice.gst > 0 && (
                    <div className="flex justify-between">
                      <span>GST (5%):</span>
                      <span>${calculatedPrice.gst.toFixed(2)}</span>
                    </div>
                  )}
                  {calculatedPrice.pst > 0 && (
                    <div className="flex justify-between">
                      <span>PST (7%):</span>
                      <span>${calculatedPrice.pst.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        /* Progress Bar */
        .progress-container {
          position: relative;
          top: 0;
          left: 0;
          right: 0;
          background: white;
          padding: 1rem 5%;
          box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          z-index: 100;
        }

        .progress-bar {
          max-width: 800px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
        }

        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          position: relative;
          z-index: 2;
        }

        .step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #d1d5db;
          border: 3px solid #d1d5db;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          font-weight: 600;
          transition: all 0.5s ease;
        }

        .step-circle.active {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
          transform: scale(1.1);
        }

        .step-circle.completed {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        .step-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #6b7280;
          text-align: center;
          transition: color 0.3s ease;
        }

        .step-label.active {
          color: var(--primary);
          font-weight: 600;
        }

        .progress-line {
          position: absolute;
          top: 20px;
          left: 0;
          right: 0;
          height: 3px;
          background: #d1d5db;
          z-index: 1;
        }

        .progress-line-fill {
          height: 100%;
          background: var(--primary);
          transition: width 0.8s ease;
        }

        /* Hero Section */
        .booking-hero {
          padding: 8rem 5% 4rem;
          margin-top: 80px;
          background: var(
            --gradient-1,
            linear-gradient(135deg, #667eea 0%, #764ba2 100%)
          );
          background-size: cover;
          background-position: center;
          color: white;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .booking-hero::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 0;
        }

        .booking-hero-content {
          position: relative;
          z-index: 1;
          max-width: 800px;
          margin: 0 auto;
        }

        .booking-hero h1 {
          font-size: clamp(1rem, 2vw, 2rem);
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .booking-hero p {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 2rem;
        }

        /* Main Content */
        .booking-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 4rem 5% 6rem;
                    margin-top: 50px;

        }

        .booking-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 3rem;
          align-items: start;
        }

        /* Booking Form */
        .booking-form {
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),
            0 8px 10px -6px rgb(0 0 0 / 0.1);
          overflow: hidden;
        }

        .form-section {
          padding: 2rem;
          border-bottom: 1px solid #e5e7eb;
          opacity: 1;
          transform: translateY(0);
          transition: all 0.5s ease;
        }

        .form-section:last-child {
          border-bottom: none;
        }

        .form-section.active {
          animation: slideInUp 0.6s ease;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--dark);
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .section-icon {
          width: 40px;
          height: 40px;
          background: var(--primary);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.1rem;
          transition: background 0.3s ease;
        }

        /* Time Slots */
        .time-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .time-card {
          border: 2px solid #d1d5db;
          border-radius: 12px;
          padding: 1rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.4s ease;
          background: white;
          opacity: 1;
          transform: scale(1);
        }

        .time-card:hover {
          border-color: var(--primary);
          transform: translateY(-3px) scale(1);
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1),
            0 4px 6px -4px rgb(0 0 0 / 0.1);
        }

        .time-card.selected {
          border-color: var(--primary);
          background: rgba(99, 102, 241, 0.05);
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1),
            0 4px 6px -4px rgb(0 0 0 / 0.1);
        }

        /* Form Elements */
        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
                    font-size: 0.9rem;

        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 2px solid #d1d5db;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          transform: translateY(-1px);
        }

        /* Submit Button */
        .submit-btn {
          background: var(--primary);
          color: white;
          padding: 1rem 2rem;
          border: none;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.4s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-width: 200px;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),
            0 8px 10px -6px rgb(0 0 0 / 0.1);
        }

        .submit-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        /* Summary Card */
        .booking-summary {
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),
            0 8px 10px -6px rgb(0 0 0 / 0.1);
          padding: 2rem;
          position: sticky;
          top: 120px;
          animation: slideInRight 0.8s ease;
        }

        .summary-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--dark);
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #e5e7eb;
          opacity: 0.5;
          transition: opacity 0.5s ease;
           }

        .summary-item.filled {
          opacity: 1;
        }

        .summary-item:last-child {
          border-bottom: none;
          font-weight: 700;
          font-size: 1.1rem;
          color: var(--primary);
        }

        .summary-label {
          color: var(--text-secondary);
        }

        .summary-value {
          font-weight: 600;
          color: var(--dark);
        }

        /* Animations */
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Mobile Responsive */
        @media (max-width: 1024px) {
          .booking-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .booking-summary {
            position: static;
            order: -1;
          }

          .progress-bar {
            flex-wrap: wrap;
            gap: 1rem;
          }

          .step-label {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 768px) {
          .booking-hero {
            padding: 6rem 5% 3rem;
          }

          .booking-container {
            padding: 2rem 5% 4rem;
          }

          .time-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .form-section {
            padding: 1.5rem;
          }

          .booking-summary {
            padding: 1.5rem;
          }

          .progress-container {
            padding: 0.5rem 5%;
          }

          .step-circle {
            width: 35px;
            height: 35px;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 1.25rem;
          }

          .time-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .progress-bar {
            justify-content: center;
          }

          .step-label {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

const BookingPage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`/api/booking-services/${slug}`);
        setService(response.data);
      } catch (error) {
        console.error("Error fetching service:", error);
        setError("Service not found");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchService();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading booking service...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Service Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The booking service you're looking for doesn't exist.
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!service.isActive) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Service Unavailable
          </h1>
          <p className="text-gray-600 mb-4">
            This booking service is currently unavailable.
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <BookingForm service={service} />
    </Elements>
  );
};

export default BookingPage;

// // wotrks properly before saba design
// // src/pages/BookingPage.js
// import React, { useState, useEffect, useCallback } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { loadStripe } from "@stripe/stripe-js";
// import {
//   Elements,
//   CardElement,
//   useStripe,
//   useElements,
// } from "@stripe/react-stripe-js";
// import axios from "axios";
// import {
//   Calendar,
//   Clock,
//   DollarSign,
//   User,
//   Mail,
//   Phone,
//   MapPin,
//   FileText,
//   CreditCard,
//   CheckCircle,
//   AlertCircle,
//   Loader2,
//   Info,
//   Car,
//   Utensils,
// } from "lucide-react";

// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// const BookingForm = ({ service }) => {
//   const stripe = useStripe();
//   const elements = useElements();
//   const navigate = useNavigate();

//   const [selectedDate, setSelectedDate] = useState("");
//   const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
//   const [availableSlots, setAvailableSlots] = useState([]);
//   const [duration, setDuration] = useState(
//     service?.timeSlots?.[0]?.duration || 60
//   );
//   const [loading, setLoading] = useState(false);
//   const [bookingLoading, setBookingLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState(false);
//   const [bookingResult, setBookingResult] = useState(null);
//   const [availableDurations, setAvailableDurations] = useState([]);

//   const [customerInfo, setCustomerInfo] = useState({
//     firstName: "",
//     lastName: "",
//     email: "",
//     phone: "",
//     company: "",
//   });

//   const [formResponses, setFormResponses] = useState({});
//   const [calculatedPrice, setCalculatedPrice] = useState({
//     basePrice: 0,
//     optionPrices: 0,
//     subtotal: 0,
//     gst: 0,
//     pst: 0,
//     totalAmount: 0,
//   });

//   const formatDateForAPI = (dateString) => {
//     // Create date in local timezone and format as YYYY-MM-DD
//     const date = new Date(dateString + "T00:00:00");
//     return date.toISOString().split("T")[0];
//   };

//   const parseLocalDate = (dateString) => {
//     // Parse date as local date, not UTC
//     const [year, month, day] = dateString.split("-");
//     return new Date(year, month - 1, day);
//   };

//   // Set up available durations based on service type
//   useEffect(() => {
//     if (service) {
//       if (
//         service.category === "driving_class" ||
//         service.pricing.pricingType === "duration_based"
//       ) {
//         // setAvailableDurations([60, 90, 120, 150, 180]);
//         const serviceDurations = service.timeSlots.map((slot) => slot.duration);
//         const uniqueDurations = [...new Set(serviceDurations)].sort(
//           (a, b) => a - b
//         );

//         setAvailableDurations(uniqueDurations);
//         setDuration(uniqueDurations[0] || 60);
//       } else {
//         setAvailableDurations([service.timeSlots[0]?.duration || 60]);
//         setDuration(service.timeSlots[0]?.duration || 60);
//       }
//     }
//   }, [service]);

//   //   // Calculate minimum date (today + minimum notice hours)
//   //   const getMinDate = () => {
//   //     const now = new Date();
//   //     const minNoticeMs = (service?.minNoticeHours || 2) * 60 * 60 * 1000;
//   //     const minDate = new Date(now.getTime() + minNoticeMs);
//   //     return minDate.toISOString().split("T")[0];
//   //   };

//   //   // Calculate maximum date (today + advance booking days)
//   //   const getMaxDate = () => {
//   //     const now = new Date();
//   //     const maxDays = service?.advanceBookingDays || 30;
//   //     const maxDate = new Date(now.getTime() + maxDays * 24 * 60 * 60 * 1000);
//   //     return maxDate.toISOString().split("T")[0];
//   //   };

//   const getMinDate = () => {
//     const now = new Date();
//     const minNoticeMs = (service?.minNoticeHours || 2) * 60 * 60 * 1000;
//     const minDate = new Date(now.getTime() + minNoticeMs);

//     // If the minimum date is in the past (same day but past the notice time), use tomorrow
//     const today = new Date();
//     today.setHours(0, 0, 0, 0);
//     const minDateOnly = new Date(minDate);
//     minDateOnly.setHours(0, 0, 0, 0);

//     if (minDateOnly <= today) {
//       const tomorrow = new Date(today);
//       tomorrow.setDate(tomorrow.getDate() + 1);
//       return tomorrow.toISOString().split("T")[0];
//     }

//     return minDate.toISOString().split("T")[0];
//   };

//   // Calculate maximum date (today + advance booking days)
//   const getMaxDate = () => {
//     const now = new Date();
//     const maxDays = service?.advanceBookingDays || 30;
//     const maxDate = new Date();
//     maxDate.setDate(now.getDate() + maxDays);
//     return maxDate.toISOString().split("T")[0];
//   };

//   // Also add this effect to ensure dates are recalculated when service changes
//   useEffect(() => {
//     if (service && selectedDate) {
//       const minDate = getMinDate();
//       const maxDate = getMaxDate();

//       // Check if selected date is still valid
//       if (selectedDate < minDate || selectedDate > maxDate) {
//         setSelectedDate("");
//         setSelectedTimeSlot(null);
//         setAvailableSlots([]);
//       }
//     }
//   }, [service]);

//   // Load available time slots when date or duration changes
//   useEffect(() => {
//     if (selectedDate && service?.slug) {
//       loadAvailableSlots();
//     }
//   }, [selectedDate, duration, service?.slug]);

//   // Calculate price when form responses change
//   useEffect(() => {
//     calculatePrice();
//   }, [formResponses, duration, service]);

//   const loadAvailableSlots = async () => {
//     setLoading(true);
//     setSelectedTimeSlot(null);
//     try {
//       const response = await axios.get(
//         `/api/booking-services/${service.slug}/availability/${selectedDate}?duration=${duration}`
//       );

//       if (response.data.available) {
//         setAvailableSlots(response.data.slots);
//         setError("");
//       } else {
//         setAvailableSlots([]);
//         setError(response.data.reason || "No available slots for this date");
//       }
//     } catch (error) {
//       console.error("Error loading slots:", error);
//       setError("Error loading available time slots");
//       setAvailableSlots([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const calculatePrice = () => {
//     if (!service) return;

//     let basePrice = service.pricing.basePrice || 0;
//     let optionPrices = 0;

//     // Duration-based pricing
//     if (service.pricing.pricingType === "duration_based") {
//       basePrice = (duration / 60) * (service.pricing.pricePerMinute || 0);
//     }

//     // Option-based pricing
//     if (service.pricing.optionPricing && formResponses) {
//       service.pricing.optionPricing.forEach((optionPrice) => {
//         const responseValue = formResponses[optionPrice.fieldId];
//         if (responseValue === optionPrice.optionValue) {
//           optionPrices += optionPrice.additionalPrice || 0;
//         }
//       });
//     }

//     const subtotal = basePrice + optionPrices;
//     const gst = service.hasGST ? subtotal * 0.05 : 0;
//     const pst = service.hasPST ? subtotal * 0.07 : 0;
//     const totalAmount = subtotal + gst + pst;

//     setCalculatedPrice({
//       basePrice,
//       optionPrices,
//       subtotal,
//       gst,
//       pst,
//       totalAmount,
//     });
//   };

//   const handleFormResponseChange = (fieldId, value) => {
//     setFormResponses((prev) => ({
//       ...prev,
//       [fieldId]: value,
//     }));
//   };

//   const validateForm = () => {
//     // Validate customer info
//     if (
//       !customerInfo.firstName ||
//       !customerInfo.lastName ||
//       !customerInfo.email
//     ) {
//       setError("Please fill in all required customer information");
//       return false;
//     }

//     // Validate email format
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (!emailRegex.test(customerInfo.email)) {
//       setError("Please enter a valid email address");
//       return false;
//     }

//     // Validate phone format if provided
//     if (customerInfo.phone && !/^[\d\s\-\+\(\)\.]+$/.test(customerInfo.phone)) {
//       setError("Please enter a valid phone number");
//       return false;
//     }

//     // Validate booking details
//     if (!selectedDate || !selectedTimeSlot) {
//       setError("Please select a date and time slot");
//       return false;
//     }

//     // Validate required form fields
//     const requiredFields = service.formFields.filter((field) => field.required);
//     for (const field of requiredFields) {
//       const value = formResponses[field.fieldId];
//       if (!value || (Array.isArray(value) && value.length === 0)) {
//         setError(`Please fill in the required field: ${field.label}`);
//         return false;
//       }
//     }

//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       return;
//     }

//     setBookingLoading(true);
//     setError("");

//     try {
//       let paymentMethodId = null;

//       // Handle payment if required
//       if (service.requiresPayment && calculatedPrice.totalAmount > 0) {
//         if (!stripe || !elements) {
//           setError("Payment system not ready. Please try again.");
//           setBookingLoading(false);
//           return;
//         }

//         const cardElement = elements.getElement(CardElement);
//         if (!cardElement) {
//           setError("Payment form not found. Please refresh and try again.");
//           setBookingLoading(false);
//           return;
//         }

//         const { error: paymentError, paymentMethod } =
//           await stripe.createPaymentMethod({
//             type: "card",
//             card: cardElement,
//             billing_details: {
//               name: `${customerInfo.firstName} ${customerInfo.lastName}`,
//               email: customerInfo.email,
//               phone: customerInfo.phone || undefined,
//             },
//           });

//         if (paymentError) {
//           setError(paymentError.message);
//           setBookingLoading(false);
//           return;
//         }

//         paymentMethodId = paymentMethod.id;
//       }

//       // Create booking
//       //   const bookingData = {
//       //     serviceSlug: service.slug,
//       //     customerInfo,
//       //     bookingDate: selectedDate,
//       //     startTime: selectedTimeSlot.startTime,
//       //     duration,
//       //     formResponses: Object.entries(formResponses).map(
//       //       ([fieldId, value]) => ({
//       //         fieldId,
//       //         value,
//       //         label:
//       //           service.formFields.find((f) => f.fieldId === fieldId)?.label ||
//       //           fieldId,
//       //       })
//       //     ),
//       //     paymentMethodId,
//       //   };
//       const bookingData = {
//         serviceSlug: service.slug,
//         customerInfo,
//         bookingDate: selectedDate + "T00:00:00", // Force local timezone interpretation
//         startTime: selectedTimeSlot.startTime,
//         duration,
//         formResponses: Object.entries(formResponses).map(
//           ([fieldId, value]) => ({
//             fieldId,
//             value,
//             label:
//               service.formFields.find((f) => f.fieldId === fieldId)?.label ||
//               fieldId,
//           })
//         ),
//         paymentMethodId,
//       };

//       const response = await axios.post("/api/bookings", bookingData);

//       if (response.data.success) {
//         setBookingResult(response.data.booking);
//         setSuccess(true);

//         // Redirect to confirmation page
//         navigate(
//           `/booking-confirmation/${response.data.booking.bookingNumber}?email=${customerInfo.email}`
//         );
//       } else {
//         setError("Booking failed. Please try again.");
//       }
//     } catch (error) {
//       console.error("Booking error:", error);
//       setError(
//         error.response?.data?.error ||
//           "An error occurred while creating your booking"
//       );
//     } finally {
//       setBookingLoading(false);
//     }
//   };

//   const renderFormField = (field) => {
//     const value = formResponses[field.fieldId] || "";

//     switch (field.type) {
//       case "text":
//       case "email":
//       case "phone":
//         return (
//           <input
//             type={field.type}
//             value={value}
//             onChange={(e) =>
//               handleFormResponseChange(field.fieldId, e.target.value)
//             }
//             placeholder={field.placeholder}
//             required={field.required}
//             className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//           />
//         );

//       case "textarea":
//         return (
//           <textarea
//             value={value}
//             onChange={(e) =>
//               handleFormResponseChange(field.fieldId, e.target.value)
//             }
//             placeholder={field.placeholder}
//             required={field.required}
//             rows="3"
//             className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//           />
//         );

//       case "number":
//         return (
//           <input
//             type="number"
//             value={value}
//             onChange={(e) =>
//               handleFormResponseChange(field.fieldId, e.target.value)
//             }
//             placeholder={field.placeholder}
//             required={field.required}
//             min={field.validation?.min}
//             max={field.validation?.max}
//             className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//           />
//         );

//       case "select":
//         return (
//           <select
//             value={value}
//             onChange={(e) =>
//               handleFormResponseChange(field.fieldId, e.target.value)
//             }
//             required={field.required}
//             className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//           >
//             <option value="">Select an option...</option>
//             {field.options.map((option, index) => (
//               <option key={index} value={option}>
//                 {option}
//               </option>
//             ))}
//           </select>
//         );

//       case "radio":
//         return (
//           <div className="space-y-2">
//             {field.options.map((option, index) => (
//               <label key={index} className="flex items-center space-x-2">
//                 <input
//                   type="radio"
//                   name={field.fieldId}
//                   value={option}
//                   checked={value === option}
//                   onChange={(e) =>
//                     handleFormResponseChange(field.fieldId, e.target.value)
//                   }
//                   required={field.required}
//                   className="text-blue-600"
//                 />
//                 <span>{option}</span>
//               </label>
//             ))}
//           </div>
//         );

//       case "checkbox":
//         return (
//           <div className="space-y-2">
//             {field.options.map((option, index) => (
//               <label key={index} className="flex items-center space-x-2">
//                 <input
//                   type="checkbox"
//                   value={option}
//                   checked={
//                     Array.isArray(value) ? value.includes(option) : false
//                   }
//                   onChange={(e) => {
//                     const currentValues = Array.isArray(value) ? value : [];
//                     const newValues = e.target.checked
//                       ? [...currentValues, option]
//                       : currentValues.filter((v) => v !== option);
//                     handleFormResponseChange(field.fieldId, newValues);
//                   }}
//                   className="text-blue-600 rounded"
//                 />
//                 <span>{option}</span>
//               </label>
//             ))}
//           </div>
//         );

//       case "date":
//         return (
//           <input
//             type="date"
//             value={value}
//             onChange={(e) =>
//               handleFormResponseChange(field.fieldId, e.target.value)
//             }
//             required={field.required}
//             className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//           />
//         );

//       case "time":
//         return (
//           <input
//             type="time"
//             value={value}
//             onChange={(e) =>
//               handleFormResponseChange(field.fieldId, e.target.value)
//             }
//             required={field.required}
//             className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//           />
//         );

//       default:
//         return null;
//     }
//   };

//   const getCategoryIcon = (category) => {
//     switch (category) {
//       case "driving_class":
//         return <Car className="w-5 h-5" />;
//       case "restaurant":
//         return <Utensils className="w-5 h-5" />;
//       default:
//         return <Calendar className="w-5 h-5" />;
//     }
//   };

//   if (success) {
//     return (
//       <div className="max-w-2xl mx-auto p-6">
//         <div className="text-center">
//           <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
//           <h2 className="text-2xl font-bold text-gray-900 mb-2">
//             Booking Confirmed!
//           </h2>
//           <p className="text-gray-600 mb-6">
//             Your booking has been successfully created.
//           </p>

//           {bookingResult && (
//             <div className="bg-gray-50 rounded-lg p-6 mb-6">
//               <h3 className="font-semibold mb-2">Booking Details</h3>
//               <p>
//                 <strong>Booking Number:</strong> {bookingResult.bookingNumber}
//               </p>
//               <p>
//                 <strong>Status:</strong> {bookingResult.status}
//               </p>
//               {bookingResult.totalAmount > 0 && (
//                 <p>
//                   <strong>Amount:</strong> $
//                   {bookingResult.totalAmount.toFixed(2)}
//                 </p>
//               )}
//             </div>
//           )}

//           <p className="text-sm text-gray-500 mb-4">
//             A confirmation email has been sent to your email address.
//           </p>

//           <button
//             onClick={() => navigate("/")}
//             className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
//           >
//             Return to Home
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gray-50 py-8">
//       <div className="max-w-4xl mx-auto p-6">
//         {/* Header */}
//         <div className="mb-8">
//           <div className="flex items-center space-x-3 mb-4">
//             {getCategoryIcon(service?.category)}
//             <h1 className="text-3xl font-bold text-gray-900">
//               {service?.name}
//             </h1>
//           </div>
//           {service?.description && (
//             <p className="text-gray-600">{service.description}</p>
//           )}

//           {/* Service Info */}
//           <div className="flex flex-wrap items-center space-x-6 mt-4 text-sm text-gray-500">
//             <span className="flex items-center">
//               <Clock className="w-4 h-4 mr-1" />
//               {service?.timeSlots?.[0]?.duration || duration} min duration
//             </span>
//             {service?.requiresPayment && (
//               <span className="flex items-center">
//                 <DollarSign className="w-4 h-4 mr-1" />
//                 Starting from $
//                 {service?.pricing?.basePrice?.toFixed(2) || "0.00"}
//               </span>
//             )}
//             <span className="flex items-center">
//               <MapPin className="w-4 h-4 mr-1" />
//               {service?.category?.replace("_", " ")}
//             </span>
//           </div>
//         </div>

//         <form onSubmit={handleSubmit} className="space-y-8">
//           {/* Customer Information */}
//           <div className="bg-white rounded-lg shadow-sm border p-6">
//             <h2 className="text-xl font-semibold mb-4 flex items-center">
//               <User className="w-5 h-5 mr-2" />
//               Customer Information
//             </h2>
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   First Name *
//                 </label>
//                 <input
//                   type="text"
//                   value={customerInfo.firstName}
//                   onChange={(e) =>
//                     setCustomerInfo({
//                       ...customerInfo,
//                       firstName: e.target.value,
//                     })
//                   }
//                   required
//                   className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Last Name *
//                 </label>
//                 <input
//                   type="text"
//                   value={customerInfo.lastName}
//                   onChange={(e) =>
//                     setCustomerInfo({
//                       ...customerInfo,
//                       lastName: e.target.value,
//                     })
//                   }
//                   required
//                   className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Email *
//                 </label>
//                 <input
//                   type="email"
//                   value={customerInfo.email}
//                   onChange={(e) =>
//                     setCustomerInfo({ ...customerInfo, email: e.target.value })
//                   }
//                   required
//                   className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Phone
//                 </label>
//                 <input
//                   type="tel"
//                   value={customerInfo.phone}
//                   onChange={(e) =>
//                     setCustomerInfo({ ...customerInfo, phone: e.target.value })
//                   }
//                   className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                 />
//               </div>
//               {service?.category === "driving_class" && (
//                 <div className="md:col-span-2">
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Company (Optional)
//                   </label>
//                   <input
//                     type="text"
//                     value={customerInfo.company}
//                     onChange={(e) =>
//                       setCustomerInfo({
//                         ...customerInfo,
//                         company: e.target.value,
//                       })
//                     }
//                     className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                     placeholder="Company name (if applicable)"
//                   />
//                 </div>
//               )}
//             </div>
//           </div>

//           {/* Date and Time Selection */}
//           <div className="bg-white rounded-lg shadow-sm border p-6">
//             <h2 className="text-xl font-semibold mb-4 flex items-center">
//               <Calendar className="w-5 h-5 mr-2" />
//               Select Date & Time
//             </h2>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Date *
//                 </label>
//                 <input
//                   type="date"
//                   value={selectedDate}
//                   onChange={(e) => setSelectedDate(e.target.value)}
//                   min={getMinDate()}
//                   max={getMaxDate()}
//                   required
//                   className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                 />
//                 <p className="text-xs text-gray-500 mt-1">
//                   Available {service?.minNoticeHours}+ hours in advance, up to{" "}
//                   {service?.advanceBookingDays} days
//                 </p>
//               </div>

//               {/* {(service?.pricing?.pricingType === "duration_based" ||
//                 service?.category === "driving_class") && (
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Duration (minutes) *
//                   </label>
//                   <select
//                     value={duration}
//                     onChange={(e) => setDuration(parseInt(e.target.value))}
//                     className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   >
//                     {availableDurations.map((dur) => (
//                       <option key={dur} value={dur}>
//                         {dur} minutes ({(dur / 60).toFixed(1)} hours)
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               )} */}
//               {(service?.pricing?.pricingType === "duration_based" ||
//                 service?.category === "driving_class") &&
//                 availableDurations.length > 1 && (
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">
//                       Duration (minutes) *
//                     </label>
//                     <select
//                       value={duration}
//                       onChange={(e) => setDuration(parseInt(e.target.value))}
//                       className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                     >
//                       {availableDurations.map((dur) => (
//                         <option key={dur} value={dur}>
//                           {dur} minutes ({(dur / 60).toFixed(1)} hours)
//                         </option>
//                       ))}
//                     </select>
//                   </div>
//                 )}
//             </div>

//             {/* Available Days Info */}
//             {service?.availableDays && service.availableDays.length > 0 && (
//               <div className="mb-4 p-3 bg-blue-50 rounded-lg">
//                 <div className="flex items-start">
//                   <Info className="w-4 h-4 text-blue-500 mr-2 mt-0.5" />
//                   <div>
//                     <p className="text-sm text-blue-700 font-medium">
//                       Available Days:
//                     </p>
//                     <p className="text-sm text-blue-600">
//                       {service.availableDays
//                         .map(
//                           (day) => day.charAt(0).toUpperCase() + day.slice(1)
//                         )
//                         .join(", ")}
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Available Time Slots */}
//             {selectedDate && (
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Available Time Slots *
//                 </label>

//                 {loading ? (
//                   <div className="flex items-center justify-center py-8">
//                     <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
//                     <span className="ml-2">Loading available slots...</span>
//                   </div>
//                 ) : availableSlots.length > 0 ? (
//                   <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
//                     {availableSlots.map((slot, index) => (
//                       <button
//                         key={index}
//                         type="button"
//                         onClick={() => setSelectedTimeSlot(slot)}
//                         className={`p-3 border rounded-lg text-sm font-medium transition-colors ${
//                           selectedTimeSlot?.startTime === slot.startTime
//                             ? "bg-blue-500 text-white border-blue-500"
//                             : "bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50"
//                         }`}
//                       >
//                         <div>{slot.startTime}</div>
//                         <div className="text-xs opacity-75">
//                           to {slot.endTime}
//                         </div>
//                       </button>
//                     ))}
//                   </div>
//                 ) : (
//                   <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
//                     <Clock className="w-8 h-8 mx-auto mb-2" />
//                     <p>{error || "No available slots for this date"}</p>
//                     <p className="text-sm mt-1">
//                       Try selecting a different date or duration
//                     </p>
//                   </div>
//                 )}
//               </div>
//             )}
//           </div>

//           {/* Custom Form Fields */}
//           {service?.formFields && service.formFields.length > 0 && (
//             <div className="bg-white rounded-lg shadow-sm border p-6">
//               <h2 className="text-xl font-semibold mb-4 flex items-center">
//                 <FileText className="w-5 h-5 mr-2" />
//                 Additional Information
//               </h2>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 {service.formFields.map((field) => (
//                   <div
//                     key={field.fieldId}
//                     className={field.type === "textarea" ? "md:col-span-2" : ""}
//                   >
//                     <label className="block text-sm font-medium text-gray-700 mb-1">
//                       {field.label}{" "}
//                       {field.required && (
//                         <span className="text-red-500">*</span>
//                       )}
//                     </label>
//                     {renderFormField(field)}
//                     {field.placeholder && field.type !== "textarea" && (
//                       <p className="text-xs text-gray-500 mt-1">
//                         {field.placeholder}
//                       </p>
//                     )}
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}

//           {/* Price Summary */}
//           {service?.requiresPayment && calculatedPrice.totalAmount > 0 && (
//             <div className="bg-white rounded-lg shadow-sm border p-6">
//               <h2 className="text-xl font-semibold mb-4 flex items-center">
//                 <DollarSign className="w-5 h-5 mr-2" />
//                 Price Summary
//               </h2>
//               <div className="space-y-3">
//                 <div className="flex justify-between text-sm">
//                   <span>Base Price:</span>
//                   <span>${calculatedPrice.basePrice.toFixed(2)}</span>
//                 </div>
//                 {calculatedPrice.optionPrices > 0 && (
//                   <div className="flex justify-between text-sm">
//                     <span>Selected Options:</span>
//                     <span>${calculatedPrice.optionPrices.toFixed(2)}</span>
//                   </div>
//                 )}
//                 <div className="flex justify-between text-sm border-t pt-2">
//                   <span>Subtotal:</span>
//                   <span>${calculatedPrice.subtotal.toFixed(2)}</span>
//                 </div>
//                 {calculatedPrice.gst > 0 && (
//                   <div className="flex justify-between text-sm">
//                     <span>GST (5%):</span>
//                     <span>${calculatedPrice.gst.toFixed(2)}</span>
//                   </div>
//                 )}
//                 {calculatedPrice.pst > 0 && (
//                   <div className="flex justify-between text-sm">
//                     <span>PST (7%):</span>
//                     <span>${calculatedPrice.pst.toFixed(2)}</span>
//                   </div>
//                 )}
//                 <div className="flex justify-between font-bold text-lg border-t pt-3">
//                   <span>Total Amount:</span>
//                   <span>${calculatedPrice.totalAmount.toFixed(2)}</span>
//                 </div>

//                 {service.paymentType === "deposit" &&
//                   service.depositAmount > 0 && (
//                     <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
//                       <p className="text-sm text-yellow-800">
//                         <strong>Today:</strong> $
//                         {service.depositAmount.toFixed(2)} (deposit)
//                       </p>
//                       <p className="text-sm text-yellow-700">
//                         Remaining $
//                         {(
//                           calculatedPrice.totalAmount - service.depositAmount
//                         ).toFixed(2)}{" "}
//                         due on service date
//                       </p>
//                     </div>
//                   )}
//               </div>
//             </div>
//           )}

//           {/* Payment Information */}
//           {service?.requiresPayment && calculatedPrice.totalAmount > 0 && (
//             <div className="bg-white rounded-lg shadow-sm border p-6">
//               <h2 className="text-xl font-semibold mb-4 flex items-center">
//                 <CreditCard className="w-5 h-5 mr-2" />
//                 Payment Information
//               </h2>
//               <div className="max-w-md">
//                 <div className="mb-3">
//                   <label className="block text-sm font-medium text-gray-700 mb-2">
//                     Card Details
//                   </label>
//                 </div>
//                 <CardElement
//                   options={{
//                     style: {
//                       base: {
//                         fontSize: "16px",
//                         color: "#424770",
//                         "::placeholder": {
//                           color: "#aab7c4",
//                         },
//                       },
//                     },
//                   }}
//                   className="p-3 border border-gray-300 rounded-lg"
//                 />
//                 <p className="text-xs text-gray-500 mt-2">
//                   Your payment information is secure and encrypted.
//                 </p>
//               </div>
//             </div>
//           )}

//           {/* Error Message */}
//           {error && (
//             <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
//               <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
//               <span className="text-red-700">{error}</span>
//             </div>
//           )}

//           {/* Submit Button */}
//           <div className="flex justify-end">
//             <button
//               type="submit"
//               disabled={bookingLoading || loading || !selectedTimeSlot}
//               className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-w-[200px] justify-center"
//             >
//               {bookingLoading ? (
//                 <Loader2 className="w-5 h-5 animate-spin" />
//               ) : (
//                 <CheckCircle className="w-5 h-5" />
//               )}
//               <span>
//                 {bookingLoading
//                   ? "Processing..."
//                   : service?.requiresPayment
//                   ? "Pay & Confirm Booking"
//                   : "Confirm Booking"}
//               </span>
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// const BookingPage = () => {
//   const { slug } = useParams();
//   const [service, setService] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");

//   useEffect(() => {
//     const fetchService = async () => {
//       try {
//         const response = await axios.get(`/api/booking-services/${slug}`);
//         setService(response.data);
//       } catch (error) {
//         console.error("Error fetching service:", error);
//         setError("Service not found");
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (slug) {
//       fetchService();
//     }
//   }, [slug]);

//   if (loading) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-gray-50">
//         <div className="text-center">
//           <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
//           <p className="text-gray-600">Loading booking service...</p>
//         </div>
//       </div>
//     );
//   }

//   if (error || !service) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-gray-50">
//         <div className="text-center">
//           <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
//           <h1 className="text-2xl font-bold text-gray-900 mb-2">
//             Service Not Found
//           </h1>
//           <p className="text-gray-600 mb-4">
//             The booking service you're looking for doesn't exist.
//           </p>
//           <button
//             onClick={() => window.history.back()}
//             className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
//           >
//             Go Back
//           </button>
//         </div>
//       </div>
//     );
//   }

//   if (!service.isActive) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-gray-50">
//         <div className="text-center">
//           <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
//           <h1 className="text-2xl font-bold text-gray-900 mb-2">
//             Service Unavailable
//           </h1>
//           <p className="text-gray-600 mb-4">
//             This booking service is currently unavailable.
//           </p>
//           <button
//             onClick={() => window.history.back()}
//             className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
//           >
//             Go Back
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <Elements stripe={stripePromise}>
//       <BookingForm service={service} />
//     </Elements>
//   );
// };

// export default BookingPage;
